import { getSession } from '../session.js';
import { showToast } from './Toast.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

interface Shift {
  id: string;
  employeeId: string;
  employeeName: string;
  role: string;
  date: string;       // YYYY-MM-DD
  startTime: string;  // HH:MM
  endTime: string;
}

interface Employee { id: string; name: string; role: string; }

const ROLE_COLOR: Record<string, string> = {
  server:     '#3b82f6',
  bartender:  '#8b5cf6',
  host:       '#10b981',
  kitchen:    '#f59e0b',
  manager:    '#ef4444',
  default:    '#64748b',
};

const SAMPLE_EMPLOYEES: Employee[] = [
  { id: 'e1', name: 'Alex Johnson',  role: 'server'    },
  { id: 'e2', name: 'Maria Santos',  role: 'server'    },
  { id: 'e3', name: 'Tom Rivera',    role: 'server'    },
  { id: 'e4', name: 'Derek Lee',     role: 'bartender' },
  { id: 'e5', name: 'Sandra Kim',    role: 'host'      },
  { id: 'e6', name: 'Chef Mike',     role: 'kitchen'   },
];

// ── Shared shift store ────────────────────────────────────────────────────────

function shiftStore(): Shift[] {
  const w = window as unknown as { __preuve_schedule?: Shift[] };
  if (!w.__preuve_schedule) {
    w.__preuve_schedule = buildSampleShifts();
  }
  return w.__preuve_schedule;
}

function saveShifts(shifts: Shift[]): void {
  (window as unknown as { __preuve_schedule: Shift[] }).__preuve_schedule = shifts;
}

// Returns employees on shift today — used by Table Management Assign Server dropdowns
export function getTodayEmployees(): Employee[] {
  const today = todayStr();
  const todayShifts = shiftStore().filter((s) => s.date === today);
  if (todayShifts.length === 0) return SAMPLE_EMPLOYEES;
  const seen = new Set<string>();
  return todayShifts
    .filter((s) => { if (seen.has(s.employeeId)) return false; seen.add(s.employeeId); return true; })
    .map((s) => ({ id: s.employeeId, name: s.employeeName, role: s.role }));
}

function todayStr(): string { return new Date().toISOString().slice(0, 10); }

function buildSampleShifts(): Shift[] {
  const monday = getWeekMonday(new Date());
  const plan: Array<{ emp: Employee; days: number[]; start: string; end: string }> = [
    { emp: SAMPLE_EMPLOYEES[0]!, days: [0,1,3,4],   start: '10:00', end: '16:00' },
    { emp: SAMPLE_EMPLOYEES[1]!, days: [2,3,4,5],   start: '16:00', end: '22:00' },
    { emp: SAMPLE_EMPLOYEES[2]!, days: [1,2,5,6],   start: '10:00', end: '16:00' },
    { emp: SAMPLE_EMPLOYEES[3]!, days: [3,4,5,6],   start: '16:00', end: '23:00' },
    { emp: SAMPLE_EMPLOYEES[4]!, days: [0,1,2,3,4], start: '11:00', end: '20:00' },
    { emp: SAMPLE_EMPLOYEES[5]!, days: [0,1,2,3,4], start:  '9:00', end: '17:00' },
  ];
  let n = 1;
  const shifts: Shift[] = [];
  plan.forEach(({ emp, days, start, end }) => {
    days.forEach((offset) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + offset);
      shifts.push({
        id: `s${n++}`, employeeId: emp.id, employeeName: emp.name,
        role: emp.role, date: d.toISOString().slice(0, 10),
        startTime: start, endTime: end,
      });
    });
  });
  return shifts;
}

function getWeekMonday(from: Date): Date {
  const d = new Date(from);
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function dayLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ── Main render ───────────────────────────────────────────────────────────────

let weekStart = getWeekMonday(new Date());
let editingShiftId: string | null = null;

export async function renderRestaurantSchedule(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  weekStart = getWeekMonday(new Date());

  const session = getSession();
  let employees: Employee[] = SAMPLE_EMPLOYEES;
  try {
    const api = getAPI();
    const raw = await api.getEmployees(session?.businessId ?? 'preview') as Array<{ id: string; name: string; role: string; active: boolean }>;
    if (raw.length > 0) employees = raw.filter((e) => e.active).map((e) => ({ id: e.id, name: e.name, role: e.role }));
  } catch { /* preview */ }

  container.innerHTML = buildShell(employees);
  wireShell(container, employees, navigate);
  renderCalendar(employees);
}

function buildShell(employees: Employee[]): string {
  return `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-calendar3"></i> Work Schedule</h1>
          <p class="page-subtitle">Manage weekly staff shifts. Today's scheduled staff appear in Table Management server assignments.</p>
        </div>
        <button class="btn btn-gold" id="add-shift-btn" style="color:#fff"><i class="bi bi-plus-lg" style="color:#fff"></i> Add Shift</button>
      </div>
    </div>

    <!-- Week nav -->
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:20px;flex-wrap:wrap">
      <button class="btn btn-ghost btn-sm" id="prev-week-btn" style="color:var(--text-primary)"><i class="bi bi-chevron-left" style="color:var(--text-primary)"></i> Prev</button>
      <div style="font-size:15px;font-weight:700" id="week-label"></div>
      <button class="btn btn-ghost btn-sm" id="today-btn" style="color:var(--text-primary)">Today</button>
      <button class="btn btn-ghost btn-sm" id="next-week-btn" style="color:var(--text-primary)">Next <i class="bi bi-chevron-right" style="color:var(--text-primary)"></i></button>
    </div>

    <!-- Legend -->
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap">
      ${Object.entries(ROLE_COLOR).filter(([k]) => k !== 'default').map(([role, color]) => `
        <div style="display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:var(--text-primary);background:${color}22;border:1px solid ${color}66;border-radius:20px;padding:4px 10px">
          <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0"></div>
          <span style="text-transform:capitalize">${role}</span>
        </div>`).join('')}
    </div>

    <!-- Calendar grid -->
    <div id="schedule-grid" style="overflow-x:auto"></div>

    <!-- Shift Modal -->
    <div id="shift-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:440px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="shift-modal-title">Add Shift</h3>
        <div class="form-row">
          <label>Employee *</label>
          <select id="shift-employee">
            <option value="">— Select —</option>
            ${employees.map((e) => `<option value="${e.id}" data-name="${escHtml(e.name)}" data-role="${escHtml(e.role)}">${escHtml(e.name)} (${capitalize(e.role)})</option>`).join('')}
          </select>
        </div>
        <div class="form-row-2">
          <div><label>Date *</label><input type="date" id="shift-date" /></div>
          <div>
            <label>Role</label>
            <select id="shift-role">
              ${Object.keys(ROLE_COLOR).filter((k) => k !== 'default').map((r) => `<option value="${r}">${capitalize(r)}</option>`).join('')}
            </select>
          </div>
        </div>
        <div class="form-row-2">
          <div><label>Start Time *</label><input type="time" id="shift-start" value="10:00" /></div>
          <div><label>End Time *</label><input type="time" id="shift-end" value="18:00" /></div>
        </div>
        <div style="display:flex;gap:10px;justify-content:space-between;margin-top:8px">
          <button class="btn btn-ghost" id="delete-shift-btn" style="display:none;color:var(--danger);border-color:var(--danger)"><i class="bi bi-trash"></i> Delete</button>
          <div style="display:flex;gap:8px;margin-left:auto">
            <button class="btn btn-ghost" id="close-shift-modal-btn">Cancel</button>
            <button class="btn btn-gold" id="save-shift-btn">Save Shift</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function wireShell(container: HTMLElement, employees: Employee[], navigate: NavigateFn): void {
  document.getElementById('prev-week-btn')!.addEventListener('click', () => {
    weekStart = new Date(weekStart); weekStart.setDate(weekStart.getDate() - 7);
    renderCalendar(employees);
  });
  document.getElementById('next-week-btn')!.addEventListener('click', () => {
    weekStart = new Date(weekStart); weekStart.setDate(weekStart.getDate() + 7);
    renderCalendar(employees);
  });
  document.getElementById('today-btn')!.addEventListener('click', () => {
    weekStart = getWeekMonday(new Date()); renderCalendar(employees);
  });
  document.getElementById('add-shift-btn')!.addEventListener('click', () => openShiftModal(null, employees));
  document.getElementById('close-shift-modal-btn')!.addEventListener('click', closeShiftModal);
  document.getElementById('save-shift-btn')!.addEventListener('click', () => saveShift(employees));
  document.getElementById('delete-shift-btn')!.addEventListener('click', () => deleteShift(employees));

  // Auto-fill role when employee selected
  document.getElementById('shift-employee')!.addEventListener('change', (e) => {
    const sel = e.target as HTMLSelectElement;
    const opt = sel.selectedOptions[0];
    const role = opt?.dataset['role'];
    if (role) (document.getElementById('shift-role') as HTMLSelectElement).value = role;
  });
}

// ── Calendar render ───────────────────────────────────────────────────────────

function renderCalendar(employees: Employee[]): void {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }

  const weekEnd = days[6]!;
  document.getElementById('week-label')!.textContent =
    `Week of ${weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;

  const shifts = shiftStore();
  const todayS = todayStr();

  const colW = 130;
  const empW = 130;

  const html = `
    <table style="border-collapse:collapse;min-width:${empW + colW * 7}px">
      <thead>
        <tr>
          <th style="width:${empW}px;padding:8px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);border-bottom:2px solid var(--border);background:var(--bg-secondary)">
            Employee
          </th>
          ${days.map((d) => {
            const isToday = formatDate(d) === todayS;
            return `<th style="width:${colW}px;padding:8px;text-align:center;font-size:11px;font-weight:700;border-bottom:2px solid var(--border);border-left:1px solid var(--border);background:${isToday ? 'var(--brand-gold)22' : 'var(--bg-secondary)'};color:${isToday ? 'var(--brand-gold)' : 'var(--text-muted)'}">
              ${dayLabel(d)}${isToday ? '<br><span style="font-size:9px;background:var(--brand-gold);color:#000;border-radius:3px;padding:1px 4px">TODAY</span>' : ''}
            </th>`;
          }).join('')}
        </tr>
      </thead>
      <tbody>
        ${employees.map((emp) => {
          const color = ROLE_COLOR[emp.role] ?? ROLE_COLOR.default!;
          return `<tr>
            <td style="padding:10px 12px;border-bottom:1px solid var(--border);background:var(--bg-card)">
              <div style="font-size:13px;font-weight:700">${escHtml(emp.name)}</div>
              <div style="font-size:10px;font-weight:600;text-transform:capitalize;color:${color}">${emp.role}</div>
            </td>
            ${days.map((d) => {
              const dateKey = formatDate(d);
              const dayShifts = shifts.filter((s) => s.employeeId === emp.id && s.date === dateKey);
              const isToday = dateKey === todayS;
              return `<td style="padding:6px;border-bottom:1px solid var(--border);border-left:1px solid var(--border);background:${isToday ? 'var(--brand-gold)08' : 'var(--bg-card)'};vertical-align:top;min-height:64px">
                ${dayShifts.map((sh) => `
                  <div class="shift-block" data-shift-id="${sh.id}"
                    style="background:${ROLE_COLOR[sh.role] ?? color}22;border:1px solid ${ROLE_COLOR[sh.role] ?? color}66;border-radius:6px;padding:5px 7px;margin-bottom:4px;cursor:pointer;font-size:11px">
                    <div style="font-weight:700;color:${ROLE_COLOR[sh.role] ?? color}">${sh.startTime}–${sh.endTime}</div>
                    <div style="color:var(--text-muted);font-size:10px;text-transform:capitalize">${sh.role}</div>
                  </div>`).join('')}
                <button class="add-shift-cell-btn" data-date="${dateKey}" data-emp-id="${emp.id}" data-emp-name="${escHtml(emp.name)}" data-emp-role="${escHtml(emp.role)}"
                  style="width:100%;padding:4px;border:1px dashed var(--border);border-radius:5px;background:none;color:var(--text-muted);font-size:11px;cursor:pointer;opacity:.6;margin-top:${dayShifts.length ? '2px' : '0'}">
                  + Add
                </button>
              </td>`;
            }).join('')}
          </tr>`;
        }).join('')}
      </tbody>
    </table>

    <!-- Hours summary row -->
    <div style="margin-top:12px;display:flex;gap:8px;flex-wrap:wrap">
      ${employees.map((emp) => {
        const weekShifts = shifts.filter((s) => s.employeeId === emp.id &&
          days.some((d) => formatDate(d) === s.date));
        const hours = weekShifts.reduce((sum, s) => {
          const [sh, sm] = s.startTime.split(':').map(Number);
          const [eh, em] = s.endTime.split(':').map(Number);
          return sum + ((eh! * 60 + em!) - (sh! * 60 + sm!)) / 60;
        }, 0);
        if (hours === 0) return '';
        const color = ROLE_COLOR[emp.role] ?? ROLE_COLOR.default!;
        return `<div style="background:var(--bg-card);border:1px solid var(--border);border-radius:8px;padding:6px 12px;font-size:12px">
          <span style="font-weight:700;color:${color}">${escHtml(emp.name.split(' ')[0]!)}</span>
          <span style="color:var(--text-muted);margin-left:6px">${hours.toFixed(1)} hrs this week</span>
        </div>`;
      }).join('')}
    </div>
  `;

  document.getElementById('schedule-grid')!.innerHTML = html;

  // Wire shift block clicks (edit)
  document.querySelectorAll('.shift-block').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset['shiftId']!;
      const shift = shiftStore().find((s) => s.id === id);
      if (shift) openShiftModal(shift, employees);
    });
  });

  // Wire add-shift-in-cell buttons
  document.querySelectorAll('.add-shift-cell-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const date = (btn as HTMLElement).dataset['date']!;
      const empId = (btn as HTMLElement).dataset['empId']!;
      const empName = (btn as HTMLElement).dataset['empName']!;
      const empRole = (btn as HTMLElement).dataset['empRole']!;
      openShiftModal(null, employees, { date, empId, empName, empRole });
    });
  });
}

// ── Shift modal ───────────────────────────────────────────────────────────────

function openShiftModal(
  shift: Shift | null,
  employees: Employee[],
  prefill?: { date: string; empId: string; empName: string; empRole: string },
): void {
  editingShiftId = shift?.id ?? null;
  (document.getElementById('shift-modal-title') as HTMLElement).textContent = shift ? 'Edit Shift' : 'Add Shift';
  (document.getElementById('delete-shift-btn') as HTMLElement).style.display = shift ? 'inline-flex' : 'none';

  const empSel = document.getElementById('shift-employee') as HTMLSelectElement;
  const dateSel = document.getElementById('shift-date') as HTMLInputElement;
  const roleSel = document.getElementById('shift-role') as HTMLSelectElement;
  const startSel = document.getElementById('shift-start') as HTMLInputElement;
  const endSel = document.getElementById('shift-end') as HTMLInputElement;

  if (shift) {
    empSel.value = shift.employeeId;
    dateSel.value = shift.date;
    roleSel.value = shift.role;
    startSel.value = shift.startTime;
    endSel.value = shift.endTime;
  } else {
    empSel.value = prefill?.empId ?? '';
    dateSel.value = prefill?.date ?? todayStr();
    roleSel.value = prefill?.empRole ?? 'server';
    startSel.value = '10:00';
    endSel.value = '18:00';
  }

  document.getElementById('shift-modal')!.style.display = 'flex';
  setTimeout(() => empSel.focus(), 50);
}

function closeShiftModal(): void {
  document.getElementById('shift-modal')!.style.display = 'none';
  editingShiftId = null;
}

function saveShift(employees: Employee[]): void {
  const empSel = document.getElementById('shift-employee') as HTMLSelectElement;
  const empId = empSel.value;
  const empName = empSel.selectedOptions[0]?.text.replace(/\s*\(.*\)/, '') ?? '';
  const date = (document.getElementById('shift-date') as HTMLInputElement).value;
  const role = (document.getElementById('shift-role') as HTMLSelectElement).value;
  const start = (document.getElementById('shift-start') as HTMLInputElement).value;
  const end = (document.getElementById('shift-end') as HTMLInputElement).value;

  if (!empId || !date || !start || !end) { showToast('All fields required', 'error'); return; }

  const shifts = shiftStore();
  if (editingShiftId) {
    const idx = shifts.findIndex((s) => s.id === editingShiftId);
    if (idx >= 0) shifts[idx] = { id: editingShiftId, employeeId: empId, employeeName: empName, role, date, startTime: start, endTime: end };
  } else {
    shifts.push({ id: `s${Date.now()}`, employeeId: empId, employeeName: empName, role, date, startTime: start, endTime: end });
  }
  saveShifts(shifts);
  closeShiftModal();
  renderCalendar(employees);
  showToast(editingShiftId ? 'Shift updated' : 'Shift added', 'success');
}

function deleteShift(employees: Employee[]): void {
  if (!editingShiftId || !confirm('Delete this shift?')) return;
  const shifts = shiftStore().filter((s) => s.id !== editingShiftId);
  saveShifts(shifts);
  closeShiftModal();
  renderCalendar(employees);
  showToast('Shift deleted', 'success');
}

function capitalize(str: string): string { return str.charAt(0).toUpperCase() + str.slice(1); }
function getAPI() { return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve; }
function escHtml(str: string): string { return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
